// The basemap's radius bands, applied to a live map (SPEC-redesign §6.2).
//
// §6.2's rules are in radius — "buildings only at ≤ 500 m", "minor roads only
// at ≤ 1 km" — and a zoom stop cannot express them, because zoom is derived
// from the radius *and* the disc's diameter (§6.3). The bands travel in the
// style JSON's own `metadata`, so the rule and the layer it governs live in one
// place; `scripts/build-map-style.ts` writes them and this reads them back.
import type { Map as MlMap, StyleSpecification } from 'maplibre-gl';

import type { RadiusBand } from '../../scripts/build-map-style.ts';
import { bandShows } from './wheel-geometry.ts';

type Banded = { 'papier:radius'?: RadiusBand };

/**
 * Called on every radius change and again after a theme switch, because
 * `setStyle` rebuilds every layer and takes the visibility with it.
 *
 * Every E2E scenario loads `{ version: 8, sources: {}, layers: [] }`, so this
 * has to be correct on a style with no layers at all: the loop simply does not
 * run, and `getLayer` guards the case where a style answered but a layer did
 * not survive it.
 */
export function applyRadiusBands(map: MlMap, radius: number): void {
  // MapLibre's types say `getStyle()` always answers. It does not: a style that
  // never loaded returns `undefined`, and reading `.layers` off it throws —
  // inside the very handler meant to keep the rest of the screen alive when the
  // basemap fails (§6.5, D-3). The cast is what makes the guard survive a
  // linter that believes the declaration.
  const style = map.getStyle() as StyleSpecification | undefined;
  for (const layer of style?.layers ?? []) {
    const band = (layer.metadata as Banded | undefined)?.['papier:radius'];
    if (!band || !map.getLayer(layer.id)) continue;
    const wanted = bandShows(band, radius) ? 'visible' : 'none';
    // Writing a value that is already there is not free: `setLayoutProperty`
    // fires `styledata`, and a `styledata` handler that writes back loops the
    // style forever — which is what kept the basemap from ever loading a single
    // tile, while every E2E scenario passed on a style with no layers in it.
    if (visibility(map, layer.id) !== wanted) map.setLayoutProperty(layer.id, 'visibility', wanted);
  }
}

/** `undefined` means the layer never said, and the default is `visible`. */
function visibility(map: MlMap, id: string): string {
  return (map.getLayoutProperty(id, 'visibility') as string | undefined) ?? 'visible';
}
